import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { parseQuery, toQuery, useQuery } from '../../Routes';
import { AutocompleteItem } from './SearchSuggestion';

type UseSearchAutocompleteParams = {
	searchUrl: string;
	fetchAutocompleteItems: (query: string) => Promise<AutocompleteItem[]>;
	normalizeQuery?: (query: string) => string;
	onFocus?: (query: string) => void;
};

export const useSearchAutocomplete = ({
	searchUrl,
	fetchAutocompleteItems,
	normalizeQuery = (query) => query,
	onFocus,
}: UseSearchAutocompleteParams) => {
	const history = useHistory();
	const [autocompleteItems, setAutocompleteItems] = useState<AutocompleteItem[]>([]);
	const { q: urlQuery } = useQuery<{ q: string }>();
	const [query, setQuery] = useState(urlQuery ?? '');
	const latestSuggestionRequestId = useRef(0);

	useEffect(() => {
		const unregister = history.listen((location, action) => {
			const q = parseQuery<{ q: string }>(location.search).q;
			setQuery(q ?? '');
			if (action === 'POP') {
				setAutocompleteItems([]);
			}
		});

		return unregister;
	}, [history]);

	const navigateToSearch = (term: string, facetCategory?: string) => {
		history.push(
			`${searchUrl}?${toQuery({
				q: term,
				facetCategory: facetCategory || undefined,
			})}`
		);
	};

	const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'Enter') {
			navigateToSearch(query);
			setAutocompleteItems([]);
		}
	};

	const handlePickSuggestion = (suggestion: AutocompleteItem) => {
		const term = normalizeQuery(suggestion.term);
		setQuery(term);
		setAutocompleteItems([]);
		navigateToSearch(term, suggestion.facetCategory);
	};

	const loadSuggestions = async (normalizedQuery: string) => {
		const requestId = ++latestSuggestionRequestId.current;
		try {
			const nextItems = await fetchAutocompleteItems(normalizedQuery);
			if (requestId !== latestSuggestionRequestId.current) return;
			setAutocompleteItems(nextItems);
		} catch {
			if (requestId !== latestSuggestionRequestId.current) return;
			// Keep the previous suggestions visible on transient request failures.
		}
	};

	const handleFocus = async (nextQuery: string) => {
		const normalizedQuery = normalizeQuery(nextQuery);
		if (autocompleteItems.length === 0) {
			void loadSuggestions(normalizedQuery);
		}
		onFocus?.(normalizedQuery);
	};

	const handleChange = (nextQuery: string) => {
		const normalizedQuery = normalizeQuery(nextQuery);
		setQuery(normalizedQuery);
		void loadSuggestions(normalizedQuery);
	};

	return {
		query,
		suggestions: autocompleteItems,
		handleFocus,
		handleChange,
		handleKeyDown,
		handlePickSuggestion,
	};
};
